import {
    AppBar,
    Toolbar,
    Stack,
    Link,
    Typography,
    useTheme,
    AppBarProps,
    Box,
    useMediaQuery,
    LinkProps,
} from '@b2b-ui';
import { flattenMessages, getCurrentLocale } from '@b2b-ui/utilities/locales';
import React, { useEffect, useState } from 'react';

import { IntlProvider, useIntl } from 'react-intl';

import { SideNav } from './SideNav/SideNav';
import { UserMenu } from './UserMenu/UserMenu';

type NavBarProps = {
    /** Left side title of the nav bar */
    navBarTitle: string;
    /** Link when clicking on the title */
    navBarTitleLink?: string;
    /** Set the locale of the navbar. You should only be using this as help for migration. otherwise you want to use B2B ui locales helpers instead and then enable enableLanguageSwitcher */
    locale?: string;
    /** Your your feedback sharing url */
    shareIdeaUrl?: string;
    /** Enable the language switcher feature, make sure you correctly always read locale from the B2B-ui helper getCurrentLocale before enabling it. */
    enableLanguageSwitcher?: boolean;
    /** Additional Props for the AppBar component */
    appBarProps?: AppBarProps;
    /** Search field if there needs be*/
    searchField?: React.ReactNode;
    /** The children of the component */
    helperSlot?: React.ReactNode;
    /** Custom Link component to be used if you are using a react router and want to avoid full page rendering*/
    LinkComponent?: React.ComponentType<LinkProps>;
    /** Callback used to handle help center click (for tracking usage) **/
    onClickHelpCenter?: (
        event: React.MouseEvent,
        clickOrigin: 'navbar' | 'ets menu'
    ) => void;
};

const NavBarComponent = ({
    navBarTitle,
    navBarTitleLink,
    shareIdeaUrl,
    enableLanguageSwitcher = false,
    appBarProps = {},
    locale: propLocale,
    LinkComponent = Link,
    searchField,
    helperSlot,
    onClickHelpCenter,
}: NavBarProps) => {
    const { formatMessage } = useIntl();
    const locale = propLocale || getCurrentLocale();
    const theme = useTheme();
    const isAboveSM = useMediaQuery(theme.breakpoints.up('sm'));
    const isAboveXs = useMediaQuery('(min-width:400px)');
    return (
        <Box
            sx={{
                paddingTop: appBarProps?.position === 'fixed' ? '64px' : 0,
                width: '100%',
            }}
        >
            <AppBar
                data-testid="B2B-NavBar-app-bar"
                position="static"
                sx={{ width: '100%,', height: '64px' }}
                {...appBarProps}
            >
                <Toolbar sx={{ px: 3, height: '100%' }}>
                    <Stack
                        flexDirection="row"
                        justifyContent="space-between"
                        alignItems="center"
                        flexGrow="1"
                    >
                        <Stack flexDirection="row" gap={2} alignItems="center">
                            <SideNav
                                onClickHelpCenter={(event: React.MouseEvent) =>
                                    onClickHelpCenter?.(event, 'ets menu')
                                }
                            />
                            {isAboveXs && (
                                <>
                                    {navBarTitleLink ? (
                                        <Typography
                                            component={LinkComponent}
                                            href={navBarTitleLink}
                                            sx={{
                                                marginTop: '0!important',
                                                '&:hover': {
                                                    color: 'primary.100',
                                                },
                                            }}
                                            color="primary.contrastText"
                                            variant="h3"
                                        >
                                            {navBarTitle}
                                        </Typography>
                                    ) : (
                                        <Typography
                                            sx={{ marginTop: '0!important' }}
                                            color="primary.contrastText"
                                            variant="h3"
                                        >
                                            {navBarTitle}
                                        </Typography>
                                    )}
                                </>
                            )}
                            {searchField}
                        </Stack>
                        <Typography
                            component="div"
                            variant="body1"
                            color="primary.contrastText"
                        >
                            <Stack direction="row" gap={4} alignItems="center">
                                {isAboveSM && helperSlot && (
                                    <Box>{helperSlot}</Box>
                                )}
                                {isAboveSM && shareIdeaUrl && (
                                    <Link
                                        component={LinkComponent}
                                        sx={{
                                            color: 'primary.contrastText',
                                            textDecorationColor:
                                                theme.palette.primary
                                                    .contrastText,
                                            '&:hover': {
                                                color: 'primary.100',
                                            },
                                        }}
                                        href={shareIdeaUrl}
                                        target="_blank"
                                        rel="noreferrer noopener"
                                    >
                                        {formatMessage({
                                            id: 'navbar.share_idea',
                                        })}
                                    </Link>
                                )}
                                {isAboveSM && (
                                    <Link
                                        component={LinkComponent}
                                        sx={{
                                            color: 'primary.contrastText',
                                            textDecorationColor:
                                                theme.palette.primary
                                                    .contrastText,
                                            '&:hover': {
                                                color: 'primary.100',
                                            },
                                        }}
                                        href={`/knowledge-base?locale=${locale}`}
                                        target="_blank"
                                        rel="noreferrer noopener"
                                        onClick={(event: React.MouseEvent) =>
                                            onClickHelpCenter?.(event, 'navbar')
                                        }
                                    >
                                        {formatMessage({
                                            id: 'navbar.help_center',
                                        })}
                                    </Link>
                                )}
                                <UserMenu
                                    isUsingLocaleAsProp={!!propLocale}
                                    enableLanguageSwitcher={
                                        enableLanguageSwitcher
                                    }
                                />
                            </Stack>
                        </Typography>
                    </Stack>
                </Toolbar>
            </AppBar>
        </Box>
    );
};

const getLocaleTranslation = async (locale: string) => {
    switch (locale) {
        case 'de':
            return import('./translations/de.json');
        case 'es':
            return import('./translations/es.json');
        case 'fr':
            return import('./translations/fr.json');
        case 'it':
            return import('./translations/it.json');
        default:
        case 'en':
            return import('./translations/en.json');
    }
};

/**
 * The navbar component all React ETS should use.
 */
export const NavBar = (navBarProps: NavBarProps) => {
    const locale = navBarProps.locale || getCurrentLocale();
    const [translations, setTranslations] = useState<Record<
        string,
        string
    > | null>(null);
    useEffect(() => {
        async function fetchTranslations() {
            const localeTranslations = await getLocaleTranslation(locale);
            setTranslations(flattenMessages(localeTranslations));
        }
        fetchTranslations();
    }, [locale]);
    if (!translations) {
        return null;
    }
    return (
        <IntlProvider locale={locale} messages={translations}>
            <NavBarComponent {...navBarProps} />
        </IntlProvider>
    );
};
